import {
  Article,
  ArticleFooter,
  ArticleImage,
  ArticleSubtitle,
  ArticleTitle,
} from "@babackapp/customer-ui/components/article";
import { Badge } from "@babackapp/customer-ui/components/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@babackapp/customer-ui/components/tooltip";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { formatPrice } from "@babackapp/return-widget-components/utils/format-price";
import { parseLineItemPriceInCents } from "@babackapp/return-widget-components/utils/get-line-item-price";
import type { OrderLineItem } from "@babackapp/return-widget-components/types";
import { InfoIcon } from "lucide-react";
import { formatVariantOptions } from "../../utils/format-variant-options";

type Props = {
  lineItem: OrderLineItem;
  currency?: string;
  isConnectedUser?: boolean;
  connectedUserTooltip?: string;
};

export function NonReturnableRow({
  lineItem,
  currency = "EUR",
  isConnectedUser = false,
  connectedUserTooltip,
}: Props) {
  const t = useCustomText();
  const options = formatVariantOptions(lineItem);
  const priceCents = parseLineItemPriceInCents(lineItem.price);
  const price = priceCents != null ? formatPrice(priceCents, currency) : null;
  const subtitle = [options, price].filter(Boolean).join(" • ") || null;

  return (
    <Article className="opacity-50 cursor-not-allowed select-none">
      <ArticleImage
        src={lineItem.image}
        layoutId={`img-${lineItem.line_item_id}`}
      />
      <ArticleTitle>{lineItem.title}</ArticleTitle>
      {subtitle && <ArticleSubtitle>{subtitle}</ArticleSubtitle>}
      {isConnectedUser && lineItem.sku && (
        <ArticleFooter className="flex items-center gap-1.5">
          <span>{lineItem.sku}</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <InfoIcon className="size-3.5 cursor-help" />
            </TooltipTrigger>
            <TooltipContent>{connectedUserTooltip}</TooltipContent>
          </Tooltip>
        </ArticleFooter>
      )}
      {/* The API exposes a boolean, never the exclusion that triggered it, so
          the badge stays generic. */}
      <Badge variant="secondary">{t("articleSelection.notReturnable")}</Badge>
    </Article>
  );
}
